/**
 * Every `<Modal>` in this app, routed through one place.
 *
 * Mantine's `fullScreen` is a plain boolean, not a responsive value like the
 * `cols={{ base: 1, sm: 2 }}` style props most other Mantine components take —
 * so making every editor dialog usable on a 390px phone ("modals and editors
 * are full-screen on phones … action buttons reachable without zooming", the
 * low-res/phone UX pass, DP-11) meant either threading a media-query hook
 * through two dozen call sites across a dozen files, or doing it once, here.
 * Every file that opens a Mantine modal imports `Modal` from here instead of
 * `@mantine/core` — same name, so no call site's JSX changed, only the import
 * line. A new modal should do the same.
 *
 * Threshold is Mantine's own `sm` (768px) — "full-screen on phones (Mantine
 * `fullScreen` below `sm`)" in the brief, literally: the 390×844 phone target
 * goes full-screen, the 768×1024 tablet target and everything wider keeps the
 * centered dialog at its normal `size`. `useMatches` — not a one-off
 * `window.innerWidth` read — is what makes it re-evaluate on resize/rotation
 * instead of only at first mount.
 *
 * A caller that already passes `fullScreen` explicitly (none do today) wins —
 * this only supplies the default.
 */
import { Modal as MantineModal, type ModalProps, useMatches } from '@mantine/core'

export function Modal({ fullScreen, ...props }: ModalProps) {
  const phone = useMatches({ base: true, sm: false })
  return <MantineModal fullScreen={fullScreen ?? phone} {...props} />
}
